import React, { useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { LogIn, Shield, Eye, EyeOff, AlertCircle, CheckCircle2, Lock, User, Mail } from 'lucide-react';
interface LoginProps {
  onLoginSuccess: () => void;
}
export function Login({
  onLoginSuccess
}: LoginProps) {
  const [step, setStep] = useState<'credentials' | '2fa'>('credentials');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const currentYear = new Date().getFullYear();
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!credentials.username || !credentials.password) {
      setError('Username dan password harus diisi');
      return;
    }
    if (credentials.username.toLowerCase() !== 'bulog' || credentials.password !== 'swasembada') {
      setError('Username atau password salah');
      return;
    }
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setStep('2fa');
    }, 1000);
  };
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otpCode];
    newOtp[index] = value;
    setOtpCode(newOtp);
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };
  const handleVerifyOtp = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const code = otpCode.join('');
    if (code.length !== 6) {
      setError('Kode OTP harus 6 digit');
      return;
    }
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      onLoginSuccess();
    }, 1000);
  };
  const handleResendOtp = () => {
    setOtpCode(['', '', '', '', '', '']);
    setError('');
    alert('Kode OTP baru telah dikirim ke email Anda');
  };
  return <div className="min-h-screen flex" data-id="element-169">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-bulog-blue via-blue-700 to-bulog-blue-dark p-12 flex-col justify-between overflow-hidden" data-id="element-170">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{
        backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/r93sCBVByqYePdrAyKqJtu/9.jpg)'
      }} data-id="element-171" />

        {/* Animated Pattern */}
        <div className="absolute inset-0 opacity-10" data-id="element-172">
          <div className="absolute inset-0 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:20px_20px]" data-id="element-173"></div>
        </div>

        {/* Content */}
        <div className="relative z-10" data-id="element-174">
          {/* Logo with White Background */}
          <div className="inline-block bg-white rounded-2xl p-6 shadow-2xl mb-8" data-id="element-175">
            <img src="https://cdn.magicpatterns.com/uploads/gKwFj36NtZysn8CWxJT9Nn/Logo-BULOG_Web-2048x650.png" alt="BULOG Logo" className="h-16 w-auto" data-id="element-176" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4" data-id="element-177">
            Selamat Datang di
            <br data-id="element-178" />
            BULOG Enterprise System
          </h1>
          <p className="text-blue-100 text-lg leading-relaxed" data-id="element-179">
            Sistem Manajemen Logistik Pangan Nasional yang terintegrasi untuk
            mendukung ketahanan pangan Indonesia.
          </p>
        </div>

        <div className="relative z-10 space-y-4" data-id="element-180">
          <div className="flex items-start gap-3 text-white" data-id="element-181">
            <CheckCircle2 className="flex-shrink-0 mt-1" size={20} data-id="element-182" />
            <div data-id="element-183">
              <h3 className="font-semibold mb-1" data-id="element-184">
                Manajemen Stok Terintegrasi
              </h3>
              <p className="text-sm text-blue-100" data-id="element-185">
                Pantau stok gudang nasional secara real-time
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 text-white" data-id="element-186">
            <CheckCircle2 className="flex-shrink-0 mt-1" size={20} data-id="element-187" />
            <div data-id="element-188">
              <h3 className="font-semibold mb-1" data-id="element-189">Distribusi & Logistik</h3>
              <p className="text-sm text-blue-100" data-id="element-190">
                Kelola distribusi pangan dengan efisien
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 text-white" data-id="element-191">
            <CheckCircle2 className="flex-shrink-0 mt-1" size={20} data-id="element-192" />
            <div data-id="element-193">
              <h3 className="font-semibold mb-1" data-id="element-194">Audit Trail Lengkap</h3>
              <p className="text-sm text-blue-100" data-id="element-195">
                Transparansi dan akuntabilitas terjamin
              </p>
            </div>
          </div>
        </div>

        <div className="relative z-10 text-white text-sm" data-id="element-196">
          © {currentYear} Perum BULOG. All rights reserved.
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 bg-gray-50" data-id="element-197">
        <div className="w-full max-w-md" data-id="element-198">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8" data-id="element-199">
            {/* Logo with White Background for Mobile */}
            <div className="inline-block bg-white rounded-2xl p-6 shadow-xl mb-4" data-id="element-200">
              <img src="https://cdn.magicpatterns.com/uploads/gKwFj36NtZysn8CWxJT9Nn/Logo-BULOG_Web-2048x650.png" alt="BULOG Logo" className="h-12 w-auto mx-auto" data-id="element-201" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-202">
              BULOG Enterprise System
            </h2>
            <p className="text-gray-600 text-sm mt-2" data-id="element-203">
              Sistem Manajemen Logistik Pangan Nasional
            </p>
          </div>

          {step === 'credentials' ? <div className="bg-white rounded-2xl shadow-xl p-8" data-id="element-204">
              {/* Header */}
              <div className="mb-8" data-id="element-205">
                <div className="flex items-center gap-3 mb-4" data-id="element-206">
                  <div className="w-12 h-12 bg-bulog-blue rounded-xl flex items-center justify-center" data-id="element-207">
                    <LogIn className="text-white" size={24} data-id="element-208" />
                  </div>
                  <div data-id="element-209">
                    <h3 className="text-2xl font-bold text-gray-900" data-id="element-210">Login</h3>
                    <p className="text-sm text-gray-600" data-id="element-211">Masuk ke akun Anda</p>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg" data-id="element-212">
                  <div className="flex items-start gap-3" data-id="element-213">
                    <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} data-id="element-214" />
                    <div data-id="element-215">
                      <h4 className="text-sm font-semibold text-red-900" data-id="element-216">
                        Error
                      </h4>
                      <p className="text-sm text-red-700 mt-1" data-id="element-217">{error}</p>
                    </div>
                  </div>
                </div>}

              {/* Form */}
              <form onSubmit={handleLogin} className="space-y-5" data-id="element-218">
                <div data-id="element-219">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-220">
                    Username
                  </label>
                  <div className="relative" data-id="element-221">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-222">
                      <User className="text-gray-400" size={20} data-id="element-223" />
                    </div>
                    <input type="text" value={credentials.username} onChange={e => setCredentials({
                  ...credentials,
                  username: e.target.value
                })} disabled={isLoading} placeholder="Masukkan username" className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bulog-blue focus:border-transparent transition-all text-gray-900 placeholder-gray-400" autoComplete="username" data-id="element-224" />
                  </div>
                </div>

                <div data-id="element-225">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-226">
                    Password
                  </label>
                  <div className="relative" data-id="element-227">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-228">
                      <Lock className="text-gray-400" size={20} data-id="element-229" />
                    </div>
                    <input type={showPassword ? 'text' : 'password'} value={credentials.password} onChange={e => setCredentials({
                  ...credentials,
                  password: e.target.value
                })} disabled={isLoading} placeholder="Masukkan password" className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bulog-blue focus:border-transparent transition-all text-gray-900 placeholder-gray-400" autoComplete="current-password" data-id="element-230" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600" aria-label={showPassword ? 'Hide password' : 'Show password'} data-id="element-231">
                      {showPassword ? <EyeOff size={20} data-id="element-232" /> : <Eye size={20} data-id="element-233" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between" data-id="element-234">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-235">
                    <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-bulog-blue focus:ring-bulog-blue" data-id="element-236" />
                    <span className="text-sm text-gray-700" data-id="element-237">Ingat saya</span>
                  </label>
                  <button type="button" className="text-sm font-medium text-bulog-blue hover:text-bulog-blue-dark transition-colors" data-id="element-238">
                    Lupa password?
                  </button>
                </div>

                <Button type="submit" className="w-full" size="lg" isLoading={isLoading} data-id="element-239">
                  {isLoading ? 'Memverifikasi...' : 'Login'}
                </Button>
              </form>

              {/* Demo Info */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-240">
                <div className="flex items-start gap-3" data-id="element-241">
                  <div className="w-8 h-8 bg-bulog-blue rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-242">
                    <CheckCircle2 className="text-white" size={16} data-id="element-243" />
                  </div>
                  <div className="flex-1" data-id="element-244">
                    <h4 className="text-sm font-semibold text-bulog-blue mb-2" data-id="element-245">
                      Demo Credentials
                    </h4>
                    <div className="text-sm text-gray-700 space-y-1" data-id="element-246">
                      <p data-id="element-247">
                        <span className="font-medium" data-id="element-248">Username:</span> bulog
                      </p>
                      <p data-id="element-249">
                        <span className="font-medium" data-id="element-250">Password:</span>{' '}
                        swasembada
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div> : <div className="bg-white rounded-2xl shadow-xl p-8" data-id="element-251">
              {/* Header */}
              <div className="text-center mb-8" data-id="element-252">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4" data-id="element-253">
                  <Shield className="text-white" size={32} data-id="element-254" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-255">
                  Verifikasi 2FA
                </h3>
                <p className="text-gray-600" data-id="element-256">
                  Masukkan kode 6 digit yang dikirim ke
                </p>
                <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg border border-blue-200" data-id="element-257">
                  <Mail className="text-bulog-blue" size={16} data-id="element-258" />
                  <span className="text-sm font-medium text-bulog-blue" data-id="element-259">
                    admin@bulog.co.id
                  </span>
                </div>
              </div>

              {/* Error Message */}
              {error && <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg" data-id="element-260">
                  <div className="flex items-start gap-3" data-id="element-261">
                    <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} data-id="element-262" />
                    <div data-id="element-263">
                      <h4 className="text-sm font-semibold text-red-900" data-id="element-264">
                        Error
                      </h4>
                      <p className="text-sm text-red-700 mt-1" data-id="element-265">{error}</p>
                    </div>
                  </div>
                </div>}

              {/* OTP Form */}
              <form onSubmit={handleVerifyOtp} className="space-y-6" data-id="element-266">
                <div data-id="element-267">
                  <label className="block text-sm font-medium text-gray-700 mb-4 text-center" data-id="element-268">
                    Kode OTP
                  </label>
                  <div className="flex justify-center gap-3" data-id="element-269">
                    {otpCode.map((digit, index) => <input key={index} id={`otp-${index}`} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(index, e.target.value)} onKeyDown={e => handleOtpKeyDown(index, e)} disabled={isLoading} className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-bulog-blue focus:ring-2 focus:ring-bulog-blue focus:outline-none transition-all" aria-label={`OTP digit ${index + 1}`} data-id="element-270" />)}
                  </div>
                </div>

                <div className="text-center" data-id="element-271">
                  <p className="text-sm text-gray-600 mb-2" data-id="element-272">
                    Tidak menerima kode?
                  </p>
                  <button type="button" onClick={handleResendOtp} className="text-sm font-medium text-bulog-blue hover:text-bulog-blue-dark transition-colors" disabled={isLoading} data-id="element-273">
                    Kirim ulang kode OTP
                  </button>
                </div>

                <div className="flex gap-3" data-id="element-274">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => {
                setStep('credentials');
                setOtpCode(['', '', '', '', '', '']);
                setError('');
              }} disabled={isLoading} data-id="element-275">
                    Kembali
                  </Button>
                  <Button type="submit" className="flex-1" isLoading={isLoading} data-id="element-276">
                    {isLoading ? 'Memverifikasi...' : 'Verifikasi'}
                  </Button>
                </div>
              </form>

              {/* Demo Info */}
              <div className="mt-6 p-3 bg-gray-50 border border-gray-200 rounded-lg text-center" data-id="element-277">
                <p className="text-xs text-gray-600" data-id="element-278">
                  💡 <strong data-id="element-279">Demo:</strong> Masukkan 6 digit angka apa saja
                  untuk verifikasi
                </p>
              </div>
            </div>}

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-gray-600" data-id="element-280">
            <p data-id="element-281">Sistem dilindungi dengan enkripsi end-to-end</p>
          </div>
        </div>
      </div>
    </div>;
}